import React, {useMemo, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {Button, Descriptions, Spin, Tag} from 'antd';
import {BCP_CHECK_RESULT} from './graphql/queries/bcpCheckResultQueries';
import {CheckResultDetailResponse} from './graphql/types/BcpCheckResultTypes';
import BcpCheckResultConfirmModal from './components/BcpCheckResultConfirmModal';
import {formatTime} from './utils/timeFormat';
import {useBcpCheckResultListStore} from './store/bcpCheckResultListStore';

const JsonDisplay: React.FC<{value?: string}> = ({value}) => {
    const formatted = useMemo(() => {
        if (!value) return '';
        try {
            return JSON.stringify(JSON.parse(value), null, 2);
        } catch {
            return value;
        }
    }, [value]);

    return (
        <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs overflow-auto max-h-96 whitespace-pre-wrap break-all m-0">
            {formatted || '-'}
        </pre>
    );
};

const BcpCheckResultDetailPage: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const resultId = Number(id);

    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const markNeedRefetch = useBcpCheckResultListStore((s) => s.markNeedRefetch);

    const {data, loading, refetch} = useQuery<CheckResultDetailResponse>(BCP_CHECK_RESULT, {
        variables: {id: resultId},
        skip: !resultId,
    });

    const result = data?.bcpCheckResult?.data;

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm px-6 pb-6 pt-4">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <Button onClick={() => navigate('/ops-tool/settlement/bcp/check-results')}>返回</Button>
                    <h2 className="text-base font-semibold m-0">差错处理详情</h2>
                </div>
                {result?.status === 0 && (
                    <Button type="primary" onClick={() => setConfirmModalVisible(true)}>确认</Button>
                )}
            </div>

            <Descriptions bordered column={2} size="small" className="mb-6">
                <Descriptions.Item label="ID">{result?.id}</Descriptions.Item>
                <Descriptions.Item label="核对任务">{result?.jobName || result?.jobId}</Descriptions.Item>
                <Descriptions.Item label="分组键" span={2}>{result?.groupKey}</Descriptions.Item>
                <Descriptions.Item label="结果码">
                    {result?.resultCodeDesc || result?.resultCode}
                </Descriptions.Item>
                <Descriptions.Item label="状态">
                    <Tag color={result?.status === 1 ? 'green' : result?.status === 2 ? 'default' : 'orange'}>
                        {result?.statusDesc || (
                            result?.status === 0 ? '待处理'
                            : result?.status === 1 ? '已确认'
                            : result?.status === 2 ? '无需确认'
                            : '-'
                        )}
                    </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="告警次数">{result?.alarmCount}</Descriptions.Item>
                <Descriptions.Item label="创建时间">{formatTime(result?.createTime)}</Descriptions.Item>
                <Descriptions.Item label="更新时间">{formatTime(result?.updateTime)}</Descriptions.Item>
            </Descriptions>

            <div className="mb-6">
                <h3 className="text-sm font-medium mb-2">差错数据</h3>
                <JsonDisplay value={result?.data} />
            </div>

            {result?.confirmUser && (
                <Descriptions bordered column={2} size="small" title="确认信息">
                    <Descriptions.Item label="确认码">{result?.confirmCodeDesc || result?.confirmCode || '-'}</Descriptions.Item>
                    <Descriptions.Item label="确认人">{result?.confirmUser}</Descriptions.Item>
                    <Descriptions.Item label="确认时间">{formatTime(result?.confirmTime)}</Descriptions.Item>
                    <Descriptions.Item label="确认描述" span={2}>{result?.confirmDesc}</Descriptions.Item>
                </Descriptions>
            )}

            <BcpCheckResultConfirmModal
                visible={confirmModalVisible}
                resultId={resultId}
                onSuccess={() => {
                    setConfirmModalVisible(false);
                    refetch();
                    markNeedRefetch();
                }}
                onCancel={() => setConfirmModalVisible(false)}
            />
        </div>
    );
};

export default BcpCheckResultDetailPage;
